import React from "react";
import {
  CandlestickSeries,
  CandleVolumeSeries,
  KagiSeries,
  RenkoSeries,
  PointAndFigureSeries,
  LineSeries,
  OHLCSeries,
  AreaSeries,
  StaticAreaSeries,
  BaselineSeries,
} from "@chartLib/lib/series";
import { curveLinear } from "d3-shape";
import { createVerticalLinearGradient, hexToRGBA } from "@chartLib/lib/utils";

// Gradient for AreaSeries (mountain)
// Most opaque at top (stop 0), then 0.3, then most transparent at bottom (stop 1)
const canvasGradient = createVerticalLinearGradient([
  { stop: 0, color: hexToRGBA("#099CFF", 0.01) },
  { stop: 0.5, color: hexToRGBA("#099CFF", 0.1) },
  { stop: 1, color: hexToRGBA("#099CFF", 0.3) },
]);

// Color accessor functions for bar coloring
const getBarFill = (d) => {
  // Use custom bar colors if available, otherwise fallback to traditional
  if (d && d.__barColors && d.__barColors.fill) {
    return d.__barColors.fill;
  }
  // Fallback to traditional (handle both full data point and OHLC object)
  const close = d.close !== undefined ? d.close : d.close;
  const open = d.open !== undefined ? d.open : d.open;
  return close > open ? "#8cc176" : "#c53717";
};

const getBarStroke = (d) => {
  // Use custom bar colors if available, otherwise fallback to traditional
  if (d && d.__barColors && d.__barColors.stroke) {
    return d.__barColors.stroke;
  }
  // Fallback to traditional
  const close = d.close !== undefined ? d.close : d.close;
  const open = d.open !== undefined ? d.open : d.open;
  return close > open ? "#8cc176" : "#c53717";
};

const getWickStroke = (d, defaultWickStroke) => {
  // Use custom wick color if available, otherwise use default
  if (d && d.__barColors && d.__barColors.wickStroke) {
    return d.__barColors.wickStroke;
  }
  return defaultWickStroke;
};

// Chart series rendering
export const SeriesRenderer = ({ chartType, themeColors }) => {
  const basicColor = themeColors.basicColor;
  const wickStroke = themeColors.wickStroke;

  switch (chartType) {
    case "candle":
      return (
        <CandlestickSeries
          wickStroke={(d) => getWickStroke(d, wickStroke)}
          stroke={getBarStroke}
          fill={getBarFill}
          opacity={1}
        />
      );
    case "hollowcandle":
      // Hollow candle: up = transparent fill, green stroke; down = red fill and stroke
      // Note: Hollow candles use bar colors for stroke, but fill is transparent when up
      return (
        <CandlestickSeries
          wickStroke={(d) => getWickStroke(d, wickStroke)}
          stroke={getBarStroke}
          fill={(d) => {
            // Preserve hollow candle behavior: transparent when up
            if (d.__barColors) {
              // If using bar colors, only show fill when down
              return d.close > d.open
                ? "transparent"
                : d.__barColors.fill || "#c53717";
            }
            return d.close > d.open ? "transparent" : "#c53717";
          }}
          opacity={1}
        />
      );
    case "bar":
      // Bar chart type uses single color (not affected by bar color provider)
      return <OHLCSeries stroke={basicColor} />;
    case "line": {
      // Mount the stroke property as an object for LineSeries
      const stroke = {
        color: basicColor,
        opacity: 1,
        width: 1,
        dash: "Solid",
      };
      return <LineSeries stroke={stroke} yAccessor={(d) => d.close} />;
    }
    case "colorbar":
      return <OHLCSeries stroke={getBarStroke} />;
    case "heikinashi":
      return (
        <CandlestickSeries
          wickStroke={wickStroke}
          stroke={(d) => (d.close > d.open ? "#A6E97A" : "#FF4B2B")}
          fill={(d) => (d.close > d.open ? "#A6E97A" : "#FF4B2B")}
        />
      );
    case "kagi":
      return (
        <KagiSeries
          stroke={{
            yang: "#75FB4C",
            yin: "#EA3323",
          }}
          fill={{
            yang: "none",
            yin: "none",
          }}
          strokeWidth={2}
        />
      );
    case "renko":
      return <RenkoSeries />;
    case "rangebars":
      return (
        <CandlestickSeries
          wickStroke={wickStroke}
          stroke={(d) => (d.close > d.open ? "#A6E97A" : "#FF4B2B")}
          fill={(d) => (d.close > d.open ? "#A6E97A" : "#FF4B2B")}
          opacity={1}
        />
      );
    case "pointfigure":
      return (
        <PointAndFigureSeries
          stroke={{
            up: "#75FB4C", // bright green for up columns
            down: "#EA3323", // bright red for down columns
          }}
          fill={{
            up: "none",
            down: "none",
          }}
          strokeWidth={2}
        />
      );
    case "mountain":
      return (
        <StaticAreaSeries
          yAccessor={(d) => d.close}
          fill="url(#MyGradient)"
          stroke="#099CFF"
          strokeWidth={2}
          interpolation={curveLinear}
          highlightOnHover={false}
          canvasGradient={canvasGradient}
          opacity={1}
        />
      );
    case "baseline":
      return (
        <BaselineSeries
          yAccessor={(d) => d.close}
          greenFill="#4CAF50"
          redFill="#F44336"
          separatorStroke={{
            color: "#FF0000", // Red for high visibility
            opacity: 1.0,
            width: 3,
            dash: "Solid",
          }}
          lineStroke={{
            color: basicColor,
            opacity: 1,
            width: 2,
            dash: "Solid",
          }}
          enabled={true}
          showHandle={true}
          showLine={true}
          interpolation={curveLinear}
          opacity={0.2}
        />
      );
    case "candlevolume": {
      // Volume candle behavior varies by theme:
      // - Dark theme: up = hollow (transparent fill) with lighter green stroke
      // - Light theme: up = filled black
      const isDarkMode = themeColors.isDark;
      return (
        <CandleVolumeSeries
          wickStroke={(d) => getWickStroke(d, wickStroke)}
          stroke={(d) => {
            if (isDarkMode) {
              // Dark mode: lighter green stroke for up, red for down
              if (d.__barColors) {
                return d.close > d.open
                  ? "#a8d89a"
                  : d.__barColors.stroke || "#c53717";
              }
              return d.close > d.open ? "#a8d89a" : "#c53717";
            } else {
              // Light mode: black stroke for up, red for down
              if (d.__barColors) {
                return d.close > d.open
                  ? "#000000"
                  : d.__barColors.stroke || "#c53717";
              }
              return d.close > d.open ? "#000000" : "#c53717";
            }
          }}
          fill={(d) => {
            if (isDarkMode) {
              // Dark mode: hollow (transparent) for up, filled for down
              if (d.__barColors) {
                return d.close > d.open
                  ? "transparent"
                  : d.__barColors.fill || "#c53717";
              }
              return d.close > d.open ? "transparent" : "#c53717";
            } else {
              // Light mode: black fill for up, red for down
              if (d.__barColors) {
                return d.close > d.open
                  ? "#000000"
                  : d.__barColors.fill || "#c53717";
              }
              return d.close > d.open ? "#000000" : "#c53717";
            }
          }}
          opacity={1}
        />
      );
    }
    default:
      return (
        <CandlestickSeries
          wickStroke={wickStroke}
          stroke={(d) => (d.close > d.open ? "#8cc176" : "#c53717")}
          fill={(d) => (d.close > d.open ? "#8cc176" : "#c53717")}
        />
      );
  }
};
